import * as THREE from "three";
import { cheapTextures, normalMapFrom, roughFrom } from "./mapTextures.js";

/**
 * Procedural, tileable WEAPON surfaces — the gun's equivalent of
 * `mapTextures` / `avatarTextures`, added 2026-08-01 (mission `graphics`).
 *
 * The weapon is the ONE object on screen in every frame of every match, in
 * first person, in a remote player's hands, and lying on the floor as a pickup
 * (`worldEntities` builds its rifle prop from the same part list). Until now
 * every weapon part wore a 64x64 flat canvas pattern — a couple of stroked
 * lines over a solid fill, albedo ONLY. No normal, no roughness, no relief: a
 * receiver and a rubber grip and a wooden stock all caught light identically,
 * which is exactly what makes a gun read as a painted toy.
 *
 * Each family here ships:
 *   - a DETAIL layer (RGBA, transparent where the part's own colour shows
 *     through) composited over the part's base colour, so `part.c` keeps
 *     tinting the surface exactly as before — no weapon changes hue;
 *   - a HEIGHT layer → a real tangent-space normal map, shared by every base
 *     colour of that family (relief does not depend on tint);
 *   - a roughness map from the same height, so machined metal, moulded polymer,
 *     oiled wood and a hot vent no longer share one specular response.
 *
 * Budget: detail/albedo 256², normal 256², roughness 128². Families are built
 * ONCE for the tab, eagerly (all of them on the first call, which happens while
 * the map is still loading) — NOT lazily per weapon tier, because a Gun Game
 * swap happens mid-fight and a 30 ms canvas build there is a visible hitch.
 * Per-base albedos are a single composite of the cached detail layer.
 * `userData.shared` keeps `disposeDeep` off all of them: weapon builds are torn
 * down on every skin change.
 */

const ALBEDO = 256;
const ROUGH = 128;

export type WeaponSurface =
  | "brushed" | "carbon" | "hazard" | "wood" | "vent" | "grid"
  | "plasma" | "chrome" | "gold" | "violet" | "red" | "plain";

interface Family {
  /** RGBA detail composited over the part colour. */
  detail: HTMLCanvasElement;
  height: HTMLCanvasElement;
  bump: number;
  roughLo: number;
  roughHi: number;
  normal: THREE.CanvasTexture | null;
  rough: THREE.CanvasTexture | null;
}

function cvs(size: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d", { willReadFrequently: true })! };
}

/** Fine random scratches — the signature of a metal that has been handled. */
function scratches(g: CanvasRenderingContext2D, n: number, len: number, col: string, w = 0.8): void {
  g.lineWidth = w;
  g.strokeStyle = col;
  for (let i = 0; i < n; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const a = (Math.random() - 0.5) * 0.5 + (Math.random() < 0.5 ? 0 : Math.PI / 2);
    const l = len * (0.3 + Math.random());
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l);
    g.stroke();
  }
}

/** Per-pixel grain straight into the ImageData (a fillRect per sample costs
 *  hundreds of ms in canvas state changes — same reason as `mapTextures`). */
function speckle(g: CanvasRenderingContext2D, n: number, alpha: number, dark: boolean): void {
  const img = g.getImageData(0, 0, ALBEDO, ALBEDO);
  const d = img.data;
  for (let i = 0; i < n; i++) {
    const j = (((Math.random() * ALBEDO) | 0) + ((Math.random() * ALBEDO) | 0) * ALBEDO) * 4;
    const v = dark ? 20 + Math.random() * 50 : 190 + Math.random() * 65;
    const a = alpha * 255;
    // composite onto whatever is there, keeping the alpha channel monotonic
    d[j] = (d[j] * d[j + 3] + v * a) / Math.max(1, d[j + 3] + a);
    d[j + 1] = (d[j + 1] * d[j + 3] + v * a) / Math.max(1, d[j + 3] + a);
    d[j + 2] = (d[j + 2] * d[j + 3] + v * a) / Math.max(1, d[j + 3] + a);
    d[j + 3] = Math.min(255, d[j + 3] + a);
  }
  g.putImageData(img, 0, 0);
}

type Draw = (d: CanvasRenderingContext2D, h: CanvasRenderingContext2D) => void;

/** Machined / brushed alloy: axial brushing, a milled panel line, hex screws. */
const drawBrushed: Draw = (d, h) => {
  for (let x = 0; x < ALBEDO; x += 2) {
    const v = Math.random();
    d.fillStyle = v > 0.5 ? `rgba(255,255,255,${0.05 + v * 0.09})` : `rgba(24,26,32,${0.05 + (1 - v) * 0.10})`;
    d.fillRect(x, 0, 2, ALBEDO);
    h.fillStyle = v > 0.5 ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";
    h.fillRect(x, 0, 2, ALBEDO);
  }
  // milled panel lines: a shallow groove with a lit lip below it
  for (const y of [46, 168]) {
    d.fillStyle = "rgba(14,16,22,0.55)"; d.fillRect(0, y, ALBEDO, 3);
    d.fillStyle = "rgba(255,255,255,0.22)"; d.fillRect(0, y + 3, ALBEDO, 1.5);
    h.fillStyle = "rgba(0,0,0,0.95)"; h.fillRect(0, y, ALBEDO, 3);
    h.fillStyle = "rgba(255,255,255,0.6)"; h.fillRect(0, y + 3, ALBEDO, 2);
  }
  // hex fasteners, countersunk
  for (const [x, y] of [[28, 96], [120, 26], [196, 148], [88, 214]] as const) {
    d.fillStyle = "rgba(10,12,16,0.65)";
    d.beginPath(); d.arc(x, y, 6, 0, 7); d.fill();
    d.fillStyle = "rgba(220,226,240,0.22)";
    d.beginPath(); d.arc(x - 1, y - 1, 4, 0, 7); d.fill();
    const dome = h.createRadialGradient(x, y, 1, x, y, 7);
    dome.addColorStop(0, "rgba(0,0,0,0.85)");
    dome.addColorStop(0.7, "rgba(255,255,255,0.55)");
    dome.addColorStop(1, "rgba(128,128,128,0)");
    h.fillStyle = dome; h.beginPath(); h.arc(x, y, 7, 0, 7); h.fill();
  }
  scratches(d, 90, 26, "rgba(255,255,255,0.14)");
  scratches(d, 40, 18, "rgba(10,12,18,0.22)");
};

/** 2x2 twill carbon weave — the real over/under, not a checkerboard. */
const drawCarbon: Draw = (d, h) => {
  const T = 8;
  for (let ty = 0; ty < ALBEDO / T; ty++) {
    for (let tx = 0; tx < ALBEDO / T; tx++) {
      const over = ((tx + ty) % 4) < 2; // 2/2 twill float
      const x = tx * T, y = ty * T;
      const g = d.createLinearGradient(x, y, over ? x + T : x, over ? y : y + T);
      g.addColorStop(0, "rgba(255,255,255,0.20)");
      g.addColorStop(0.5, "rgba(255,255,255,0.02)");
      g.addColorStop(1, "rgba(0,0,0,0.30)");
      d.fillStyle = g; d.fillRect(x, y, T, T);
      const hg = h.createLinearGradient(x, y, over ? x + T : x, over ? y : y + T);
      hg.addColorStop(0, "rgba(255,255,255,0.55)");
      hg.addColorStop(1, "rgba(0,0,0,0.55)");
      h.fillStyle = hg; h.fillRect(x, y, T, T);
      // the tow gap
      d.fillStyle = "rgba(0,0,0,0.34)";
      if (over) d.fillRect(x, y + T - 1.2, T, 1.2); else d.fillRect(x + T - 1.2, y, 1.2, T);
      h.fillStyle = "rgba(0,0,0,0.8)";
      if (over) h.fillRect(x, y + T - 1.2, T, 1.2); else h.fillRect(x + T - 1.2, y, 1.2, T);
    }
  }
  // clear-coat sheen so carbon reads glossy rather than as fabric
  const sheen = d.createLinearGradient(0, 0, ALBEDO, ALBEDO);
  sheen.addColorStop(0, "rgba(255,255,255,0.10)");
  sheen.addColorStop(0.55, "rgba(255,255,255,0)");
  sheen.addColorStop(1, "rgba(255,255,255,0.06)");
  d.fillStyle = sheen; d.fillRect(0, 0, ALBEDO, ALBEDO);
};

/** Rubber/polymer GRIP stipple — the surface every pistol grip actually has. */
const drawStipple: Draw = (d, h) => {
  speckle(d, 26000, 0.18, true);
  for (let i = 0; i < 1500; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1.2 + Math.random() * 2.2;
    d.fillStyle = "rgba(255,255,255,0.13)";
    d.beginPath(); d.arc(x - r * 0.25, y - r * 0.25, r * 0.7, 0, 7); d.fill();
    d.fillStyle = "rgba(0,0,0,0.20)";
    d.beginPath(); d.arc(x + r * 0.3, y + r * 0.3, r * 0.55, 0, 7); d.fill();
    const dome = h.createRadialGradient(x, y, 0, x, y, r);
    dome.addColorStop(0, "rgba(255,255,255,0.8)");
    dome.addColorStop(1, "rgba(128,128,128,0)");
    h.fillStyle = dome; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
  }
};

/** Diagonal HAZARD stripes with chipped paint showing bare alloy at the edges. */
const drawHazard: Draw = (d, h) => {
  d.lineWidth = 16; h.lineWidth = 16;
  d.strokeStyle = "rgba(18,16,10,0.80)";
  h.strokeStyle = "rgba(70,70,70,1)";
  for (let x = -ALBEDO; x < ALBEDO * 2; x += 40) {
    d.beginPath(); d.moveTo(x, ALBEDO); d.lineTo(x + ALBEDO, 0); d.stroke();
    h.beginPath(); h.moveTo(x, ALBEDO); h.lineTo(x + ALBEDO, 0); h.stroke();
  }
  // chipping: bright alloy nicks along the paint edges
  for (let i = 0; i < 220; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1 + Math.random() * 3;
    d.fillStyle = "rgba(196,204,220,0.35)";
    d.beginPath(); d.arc(x, y, r, 0, 7); d.fill();
  }
  scratches(d, 60, 22, "rgba(255,255,255,0.16)");
};

/** Oiled wood: grain lines that wander, open pores, a varnish sheen. */
const drawWood: Draw = (d, h) => {
  for (let y = -6; y < ALBEDO + 6; y += 7) {
    const amp = 2 + Math.random() * 4, ph = Math.random() * 6;
    for (const [ctx, col, w] of [
      [d, `rgba(38,20,8,${0.22 + Math.random() * 0.24})`, 1.6] as const,
      [h, "rgba(0,0,0,0.35)", 2] as const,
    ]) {
      ctx.lineWidth = w; ctx.strokeStyle = col;
      ctx.beginPath();
      for (let x = 0; x <= ALBEDO; x += 8) ctx.lineTo(x, y + Math.sin(x * 0.028 + ph) * amp);
      ctx.stroke();
    }
    d.lineWidth = 1; d.strokeStyle = "rgba(255,214,150,0.16)";
    d.beginPath();
    for (let x = 0; x <= ALBEDO; x += 8) ctx0(d, x, y + 2.4 + Math.sin(x * 0.028 + ph) * amp);
    d.stroke();
  }
  // open pores
  for (let i = 0; i < 500; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    d.fillStyle = "rgba(30,14,4,0.30)";
    d.fillRect(x, y, 1.6 + Math.random() * 2.6, 1.2);
    h.fillStyle = "rgba(0,0,0,0.5)";
    h.fillRect(x, y, 1.6 + Math.random() * 2.6, 1.2);
  }
  const sheen = d.createLinearGradient(0, 0, ALBEDO * 0.6, ALBEDO);
  sheen.addColorStop(0, "rgba(255,226,178,0.14)");
  sheen.addColorStop(1, "rgba(60,30,10,0.10)");
  d.fillStyle = sheen; d.fillRect(0, 0, ALBEDO, ALBEDO);
};
/** `lineTo` helper kept separate so the wood pass reads as one loop. */
function ctx0(g: CanvasRenderingContext2D, x: number, y: number): void { g.lineTo(x, y); }

/** Machined COOLING FINS — deep square-cut ribs, the shroud/suppressor look. */
const drawVent: Draw = (d, h) => {
  d.fillStyle = "rgba(255,255,255,0.05)"; d.fillRect(0, 0, ALBEDO, ALBEDO);
  for (let y = 0; y < ALBEDO; y += 16) {
    d.fillStyle = "rgba(0,0,0,0.62)"; d.fillRect(0, y, ALBEDO, 7);
    d.fillStyle = "rgba(255,255,255,0.20)"; d.fillRect(0, y + 7, ALBEDO, 2);
    d.fillStyle = "rgba(160,170,190,0.10)"; d.fillRect(0, y + 9, ALBEDO, 5);
    h.fillStyle = "rgba(0,0,0,1)"; h.fillRect(0, y, ALBEDO, 7);
    h.fillStyle = "rgba(255,255,255,0.85)"; h.fillRect(0, y + 7, ALBEDO, 7);
  }
  scratches(d, 50, 20, "rgba(255,255,255,0.10)");
};

/** Energy panel: a circuit grid with lit conductors and a dark substrate. */
function drawEnergy(tint: string): Draw {
  return (d, h) => {
    d.fillStyle = "rgba(6,10,18,0.35)"; d.fillRect(0, 0, ALBEDO, ALBEDO);
    d.lineWidth = 3; h.lineWidth = 3;
    for (let i = 0; i < ALBEDO; i += 32) {
      d.strokeStyle = tint; h.strokeStyle = "rgba(255,255,255,0.75)";
      d.beginPath(); d.moveTo(i, 0); d.lineTo(i, ALBEDO); d.stroke();
      h.beginPath(); h.moveTo(i, 0); h.lineTo(i, ALBEDO); h.stroke();
      d.beginPath(); d.moveTo(0, i); d.lineTo(ALBEDO, i); d.stroke();
      h.beginPath(); h.moveTo(0, i); h.lineTo(ALBEDO, i); h.stroke();
    }
    // nodes where conductors cross
    for (let y = 0; y < ALBEDO; y += 32) for (let x = 0; x < ALBEDO; x += 32) {
      if ((x + y) % 64) continue;
      d.fillStyle = "rgba(255,255,255,0.55)";
      d.beginPath(); d.arc(x, y, 4, 0, 7); d.fill();
      h.fillStyle = "rgba(255,255,255,0.9)";
      h.beginPath(); h.arc(x, y, 4, 0, 7); h.fill();
    }
    // scanline shimmer
    for (let y = 0; y < ALBEDO; y += 4) {
      d.fillStyle = "rgba(255,255,255,0.05)"; d.fillRect(0, y, ALBEDO, 1);
    }
  };
}

/** Polished chrome: a broad sweep with fine swirl scratches. */
const drawChrome: Draw = (d, h) => {
  const g = d.createLinearGradient(0, 0, ALBEDO, ALBEDO);
  g.addColorStop(0, "rgba(255,255,255,0.55)");
  g.addColorStop(0.38, "rgba(30,40,60,0.32)");
  g.addColorStop(0.62, "rgba(255,255,255,0.42)");
  g.addColorStop(1, "rgba(0,0,0,0.28)");
  d.fillStyle = g; d.fillRect(0, 0, ALBEDO, ALBEDO);
  d.lineWidth = 0.7;
  for (let i = 0; i < 260; i++) {
    const cx = Math.random() * ALBEDO, cy = Math.random() * ALBEDO, r = 8 + Math.random() * 40;
    d.strokeStyle = Math.random() < 0.5 ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.10)";
    d.beginPath(); d.arc(cx, cy, r, 0, 0.5 + Math.random()); d.stroke();
    h.strokeStyle = "rgba(255,255,255,0.10)"; h.lineWidth = 0.7;
    h.beginPath(); h.arc(cx, cy, r, 0, 0.5); h.stroke();
  }
};

/** Engraved gold: scroll-work valleys with polished ridges between them. */
const drawGold: Draw = (d, h) => {
  d.lineWidth = 2.4; h.lineWidth = 2.4;
  for (let x = -ALBEDO; x < ALBEDO * 2; x += 22) {
    d.strokeStyle = "rgba(78,46,8,0.55)"; h.strokeStyle = "rgba(0,0,0,0.7)";
    d.beginPath(); d.moveTo(x, ALBEDO); d.lineTo(x + 70, 0); d.stroke();
    h.beginPath(); h.moveTo(x, ALBEDO); h.lineTo(x + 70, 0); h.stroke();
    d.strokeStyle = "rgba(255,246,196,0.40)"; h.strokeStyle = "rgba(255,255,255,0.6)";
    d.beginPath(); d.moveTo(x + 3, ALBEDO); d.lineTo(x + 73, 0); d.stroke();
    h.beginPath(); h.moveTo(x + 3, ALBEDO); h.lineTo(x + 73, 0); h.stroke();
  }
  // engraved rosettes
  for (const [cx, cy] of [[54, 62], [180, 190]] as const) {
    for (let a = 0; a < 6; a++) {
      d.strokeStyle = "rgba(80,50,10,0.5)"; d.lineWidth = 2;
      d.beginPath(); d.arc(cx, cy, 8 + a * 3.5, a, a + 2.6); d.stroke();
      h.strokeStyle = "rgba(0,0,0,0.55)"; h.lineWidth = 2;
      h.beginPath(); h.arc(cx, cy, 8 + a * 3.5, a, a + 2.6); h.stroke();
    }
  }
  const sheen = d.createLinearGradient(0, 0, 0, ALBEDO);
  sheen.addColorStop(0, "rgba(255,248,205,0.22)");
  sheen.addColorStop(1, "rgba(120,80,10,0.12)");
  d.fillStyle = sheen; d.fillRect(0, 0, ALBEDO, ALBEDO);
};

/** Painted panel: masked bands, orange-peel texture and chipped edges. */
const drawPainted: Draw = (d, h) => {
  for (let x = 0; x < ALBEDO; x += 64) {
    d.fillStyle = "rgba(255,255,255,0.16)"; d.fillRect(x, 0, 26, ALBEDO);
    d.fillStyle = "rgba(0,0,0,0.14)"; d.fillRect(x + 26, 0, 6, ALBEDO);
    h.fillStyle = "rgba(190,190,190,1)"; h.fillRect(x, 0, 26, ALBEDO);
    h.fillStyle = "rgba(40,40,40,1)"; h.fillRect(x + 26, 0, 6, ALBEDO);
  }
  speckle(d, 14000, 0.10, false); // orange peel
  for (let i = 0; i < 160; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    d.fillStyle = "rgba(180,188,204,0.30)";
    d.beginPath(); d.arc(x, y, 0.8 + Math.random() * 2.4, 0, 7); d.fill();
  }
};

/** Bead-blasted alloy — the fallback for every part with no explicit pattern.
 *  Subtle on purpose: it must not repaint a part, only stop it being mirror
 *  flat. Before this, ~40% of every weapon's parts had NO map at all. */
const drawPlain: Draw = (d, h) => {
  speckle(d, 30000, 0.08, false);
  speckle(d, 22000, 0.08, true);
  for (let i = 0; i < 2600; i++) {
    const x = (Math.random() * ALBEDO) | 0, y = (Math.random() * ALBEDO) | 0;
    const v = Math.random() < 0.5 ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)";
    h.fillStyle = v; h.fillRect(x, y, 2, 2);
  }
  scratches(d, 45, 20, "rgba(255,255,255,0.10)");
};

const SPECS: Record<WeaponSurface, { draw: Draw; bump: number; roughLo: number; roughHi: number }> = {
  brushed: { draw: drawBrushed, bump: 1.7, roughLo: 0.22, roughHi: 0.66 },
  carbon: { draw: drawCarbon, bump: 1.5, roughLo: 0.18, roughHi: 0.5 },
  hazard: { draw: drawHazard, bump: 1.2, roughLo: 0.34, roughHi: 0.78 },
  wood: { draw: drawWood, bump: 1.6, roughLo: 0.3, roughHi: 0.72 },
  vent: { draw: drawVent, bump: 3.0, roughLo: 0.3, roughHi: 0.85 },
  grid: { draw: drawEnergy("rgba(190,240,255,0.8)"), bump: 1.4, roughLo: 0.16, roughHi: 0.55 },
  plasma: { draw: drawEnergy("rgba(190,250,255,0.85)"), bump: 1.4, roughLo: 0.16, roughHi: 0.55 },
  violet: { draw: drawEnergy("rgba(236,210,255,0.85)"), bump: 1.4, roughLo: 0.16, roughHi: 0.55 },
  chrome: { draw: drawChrome, bump: 0.8, roughLo: 0.06, roughHi: 0.3 },
  gold: { draw: drawGold, bump: 1.5, roughLo: 0.1, roughHi: 0.42 },
  red: { draw: drawPainted, bump: 1.1, roughLo: 0.26, roughHi: 0.66 },
  plain: { draw: drawPlain, bump: 1.1, roughLo: 0.28, roughHi: 0.7 },
};

/** The polymer-grip family, selected by `materialForPart` for grip-shaped
 *  parts. Kept out of `WeaponSurface`'s public list only because no part list
 *  names it directly — see `surfaceForPart`. */
SPECS.carbon = SPECS.carbon; // (no-op: keeps the table literal readable)

let families: Record<WeaponSurface, Family> | null = null;
/** Extra family, resolved by shape rather than by the part's `t` key. */
let stippleFam: Family | null = null;

function mk(canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.userData.shared = true; // weapon builds are disposed on every skin change
  return t;
}

function buildFamily(spec: { draw: Draw; bump: number; roughLo: number; roughHi: number }, cheap: boolean): Family {
  const { c: dc, g: dg } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  spec.draw(dg, hg);
  return {
    detail: dc, height: hc, bump: spec.bump, roughLo: spec.roughLo, roughHi: spec.roughHi,
    normal: cheap ? null : mk(normalMapFrom(hc, spec.bump, ALBEDO), false),
    // roughness comes from the HEIGHT layer, not the detail layer: the detail
    // is mostly transparent, so its luminance says nothing about gloss.
    rough: cheap ? null : mk(roughFrom(hc, spec.roughLo, spec.roughHi, ROUGH), false),
  };
}

function all(): Record<WeaponSurface, Family> {
  if (!families) {
    const t0 = performance.now();
    const cheap = cheapTextures();
    const out = {} as Record<WeaponSurface, Family>;
    for (const k of Object.keys(SPECS) as WeaponSurface[]) out[k] = buildFamily(SPECS[k], cheap);
    stippleFam = buildFamily({ draw: drawStipple, bump: 2.2, roughLo: 0.5, roughHi: 0.95 }, cheap);
    families = out;
    console.log(`[weptex] generated ${Object.keys(out).length + 1} weapon families in ${(performance.now() - t0).toFixed(0)} ms`);
  }
  return families;
}

/** Composited albedo per (family, base colour) — the ONLY per-colour work, and
 *  it is one fill + one drawImage.
 *
 *  Downsampled to 128²: there are ~57 (family, colour) pairs across the weapon
 *  ladder, and at 256² that is ~15 MB of GPU texture for the guns alone on a
 *  machine this game is supposed to run on. The CRISPNESS lives in the 256²
 *  normal map, which is shared per family and does not multiply by colour; the
 *  albedo only has to carry the tint and its scratches. */
const ALBEDO_OUT = 128;
const albedoCache = new Map<string, THREE.CanvasTexture>();
function albedo(fam: Family, key: string, base: string): THREE.CanvasTexture {
  let t = albedoCache.get(key);
  if (!t) {
    const { c, g } = cvs(ALBEDO_OUT);
    g.fillStyle = base;
    g.fillRect(0, 0, ALBEDO_OUT, ALBEDO_OUT);
    g.drawImage(fam.detail, 0, 0, ALBEDO_OUT, ALBEDO_OUT);
    t = mk(c, true);
    albedoCache.set(key, t);
  }
  return t;
}

/** Shared per-family normalScale vectors — never allocated per material. */
const normalScales = new Map<string, THREE.Vector2>();

/** Build the shared weapon material families before the first match. Per-colour
 * albedos are still composed by the weapon/viewmodel builders, but the heavy
 * family detail, normal and roughness canvases are generated here. */
export function prewarmWeaponTextures(): void {
  all();
}

/**
 * Fold a weapon family's albedo/normal/roughness into MeshStandardMaterial
 * params — the weapon-side twin of `avatarMaps`, so no call site can wire the
 * albedo and forget the detail maps.
 *
 * `kind` selects the pattern, `base` is the part's authored colour (it is
 * baked into the albedo, and the material colour becomes white, exactly like
 * the old `makePatternTexture` contract).
 */
export function weaponMaps(
  kind: WeaponSurface | "stipple",
  base: string,
  params: THREE.MeshStandardMaterialParameters,
  strength = 1,
): THREE.MeshStandardMaterialParameters {
  const fam = kind === "stipple" ? (all(), stippleFam!) : all()[kind];
  params.map = albedo(fam, `${kind}:${base}`, base);
  params.color = "#ffffff";
  if (fam.normal) {
    params.normalMap = fam.normal;
    const sk = `${kind}|${strength}`;
    let s = normalScales.get(sk);
    if (!s) { s = new THREE.Vector2(strength, strength); normalScales.set(sk, s); }
    params.normalScale = s;
  }
  if (fam.rough) params.roughnessMap = fam.rough;
  return params;
}
